// Real case-study content, sourced from each client's live site (Aug 2026).
// Every headline, CTA and feature pill below is lifted from the client's own copy.
export const CASE_STUDIES = {
  brightridge: {
    slug: 'brightridge', name: 'Brightridge',
    tagline: 'The right leaders for the homes that need them most.',
    discipline: ['Software', 'Web', 'Automation', 'Brand'],
    sector: 'Regulated Care Recruitment', year: '2025',
    accent: '#7B3DFF', url: 'brightridge.uk',
    shot: '/case-shots/brightridge.webp',
    card: {
      resultLine: 'Brand + platform + Pathways learning app',
      chips: ['Managers', 'Deputies', 'Ofsted-registered', 'Pathways'],
      stats: [
        { k: 'Modules', v: '12' },
        { k: 'AI Coach', v: 'Live' },
        { k: 'Dashboards', v: '2-sided' },
      ],
      callout: '"Serious sector business, not a Squarespace + Bullhorn duct-tape job."',
    },
    brief: "A specialist recruiter for UK regulated care needed more than a website — they needed a live platform. Off-market roles, Ofsted-aware screening, and a genuinely useful free learning hub for care professionals (Pathways) all had to sit alongside a lead-magnet brand that would help homes and candidates trust them fast.",
    built: [
      'End-to-end brand identity — confident, sector-specialist, calmly authoritative',
      'Website + admin CRM with candidate + provider workflows',
      'Brightridge Pathways — a free learning platform with 12 refresher modules, 3 self-assessments and an AI-driven Care Interview Coach',
      'Ofsted-aware application + safeguarding-conscious data model',
      'Provider dashboard, candidate dashboard, and a shared internal ops hub',
      'Sector-first content system — deep, not wide',
    ],
    what: 'A recruitment agency that reads like a serious sector business, powered by a custom platform we designed and built. Not a Squarespace + Bullhorn duct-tape job.',
    proof: 'Live at brightridge.uk with a fully working Pathways learning platform, AI Care Interview Coach, provider + candidate dashboards, and a UK-wide leadership pipeline.',
    hero: {
      eyebrow: 'Specialist recruitment · Regulated care',
      title: 'The right leaders for the homes that need them most.',
      titleItalicWord: 'most',
      sub: 'Off-market leadership roles across children\'s homes, care homes and supported living. Ofsted-aware from day one.',
      ctas: [{ l: 'Register a role', primary: true }, { l: 'Join Pathways' }],
      pills: ['Managers','Deputies','Team Leaders','Ofsted-registered'],
    },
    highlights: [
      { h: 'Brightridge Pathways', p: 'A free learning platform with 12 refresher modules, 3 self-assessments and an AI-driven Care Interview Coach — turned into the strongest lead magnet in the sector.' },
      { h: 'Off-market leadership pipeline', p: 'Provider workflows tuned for Ofsted-registered roles: safeguarding-conscious data model, right-to-work automation, sector-specific screening.' },
      { h: 'Two-sided dashboards', p: 'Providers see live shortlists. Candidates see their application status, next actions and Pathways progress — one platform, one story.' },
    ],
    gallery: ['Website','Provider dashboard','Candidate dashboard','Pathways platform','AI Interview Coach','Brand system'],
  },

  sugaspice: {
    slug: 'sugaspice', name: 'SugaSpice',
    tagline: 'Fresh bakes. Big flavour. Made to crave.',
    discipline: ['Brand Support', 'Web', 'Wholesale CRM'],
    sector: 'Bakery & Kitchen — St Neots', year: '2025',
    accent: '#E85D2F', url: 'sugaspice.uk',
    shot: '/case-shots/sugaspice.webp',
    card: {
      resultLine: 'Brand support · Website · Custom Wholesale CRM',
      chips: ['Signature Bakes', 'Breakfasts', 'Street Food', 'Wholesale'],
      stats: [
        { k: 'Categories', v: '6' },
        { k: 'Menu SEO pages', v: '14' },
        { k: 'Wholesale CRM', v: 'Custom' },
      ],
      callout: '"A proper independent food brand, not another Wix template."',
    },
    brief: "A modern artisan bakery + hot kitchen opening in St Neots, competing with chain coffee shops and mediocre takeaways. Needed brand support to sharpen an already-strong visual direction, a website that could sell online direct (not just Uber Eats), and — critically — a wholesale operation that could scale without spreadsheets collapsing.",
    built: [
      'Brand support &amp; direction — informing the visual system, food photography direction, tone of voice',
      'Website with a menu system, direct online ordering (Flipdish integration) and location experience',
      'Custom Wholesale CRM — sale-or-return terms, refrigerated daily deliveries, route planning, partner onboarding',
      "&lsquo;Own a shop?&rsquo; wholesale enquiry funnel wired into the CRM",
      'Signature-cravings module + local SEO across 14 menu-category landing pages',
      'Socials — flagged as the next opportunity, not yet in-scope',
    ],
    what: "A bakery that feels like a proper independent food brand — not another Wix template. Six 'obsessions' from breakfast to Indian street food, sold from one confident storefront, with a wholesale channel running quietly in the background on our own CRM.",
    proof: 'Live at sugaspice.uk with direct-order flow, wholesale enquiry pipeline, custom Wholesale CRM in production, and a wholesale partner network across St Neots, Cambridgeshire and Huntingdonshire.',
    hero: {
      eyebrow: 'Bakery & Kitchen · St Neots',
      title: 'Fresh bakes. Big flavour. Made to crave.',
      titleItalicWord: 'crave',
      sub: 'Six obsessions under one roof — signature bakes, breakfasts, Indian street food, hot roasts and everything in between.',
      ctas: [{ l: 'Order Online', primary: true }, { l: 'Visit the Bakery' }],
      pills: ['Signature Bakes','Breakfasts','Street Food','Hot Kitchen','Wholesale'],
    },
    highlights: [
      { h: 'Six obsessions, one brand', p: 'From cinnamon buns and sourdough to biryanis and Sunday roasts. Each category has its own landing page and its own SEO footprint — but all wear one confident food identity.' },
      { h: 'Direct-order, not takeaway middlemen', p: 'Flipdish-powered ordering wired into the site, not funnelled through Uber Eats. Better margin, better data, better relationship with the customer.' },
      { h: 'Wholesale on a proper CRM', p: 'We built them a bespoke Wholesale CRM — partner accounts, sale-or-return terms, refrigerated daily delivery routes and enquiry pipeline. Quietly one of the fastest-growing sides of the business.' },
    ],
    gallery: ['Brand direction','Menu system','Order flow','Wholesale CRM','Delivery routing','Category SEO pages'],
  },

  'cobra-thai': {
    slug: 'cobra-thai', name: 'Cobra Thai',
    tagline: "Pune's premier MMA & fitness community.",
    titleItalicWord: 'community',
    discipline: ['Brand', 'Web', 'Product', 'Content'],
    sector: 'MMA & Fitness Academy — Kharadi, Pune', year: '2024–2025',
    accent: '#3B4DFF', url: 'cobra-thai.com',
    shot: '/case-shots/cobra-thai.webp',
    card: {
      resultLine: 'Brand + site + members PWA + sub-brand kitchen',
      chips: ['Muay Thai', 'BJJ', 'MMA', 'Boxing'],
      stats: [
        { k: 'Disciplines', v: '6' },
        { k: 'Tiers', v: '3' },
        { k: 'Members app', v: 'PWA' },
      ],
      callout: '"A real fight brand — with the digital product to match."',
    },
    brief: "A serious MMA + Muay Thai academy in Pune that needed to look and feel like a real international fight team, not another local gym. Six disciplines to sell across three membership tiers, a members-only training app that had to work like a real product, plus in-house cafe brand (MacroBeast), events, seminars and international fighter camps.",
    built: [
      'Full brand system for Cobra Thai + sub-brand for MacroBeast Kitchen',
      'Website + booking + membership-tier flow with Razorpay checkout',
      "&lsquo;Game Plan&rsquo; — a free members-only PWA training app with tier-aware plans, PBs, arcade, streaks and skill tracker",
      'India Fight Calendar aggregator across every discipline',
      'Events + landing pages for grappling series & international seminars',
      'Content system covering fighter transformations, class schedules and facility showcase',
    ],
    what: 'An academy that reads like a proper fight brand — with the digital product to match. Members get a real app; the gym gets a system that runs alongside them.',
    proof: 'Live at cobra-thai.com. Members-only PWA in production, event landing pages, transformation showcase and integrated payments running through the site.',
    hero: {
      eyebrow: 'MMA & Fitness Academy · Kharadi, Pune',
      title: "Pune's premier MMA & fitness community.",
      titleItalicWord: 'community',
      sub: 'Six disciplines. Three tiers. One serious training environment — with a members-only app to keep you sharp between sessions.',
      ctas: [{ l: 'Book a trial class', primary: true }, { l: 'Meet the coaches' }],
      pills: ['Muay Thai','BJJ','MMA','Boxing','Wrestling','Cardio Combat'],
    },
    highlights: [
      { h: '&lsquo;Game Plan&rsquo; members app', p: 'A free tier-aware PWA that turns the gym into a product — training plans, PB tracker, streaks, arcade, and a skill tracker across all six disciplines.' },
      { h: 'MacroBeast in-house kitchen', p: 'Sub-brand for the fighters&rsquo; kitchen on site — separate brand system, macros-first menu design, integrated into the member flow.' },
      { h: 'Events + seminars engine', p: 'Landing pages that let them run international grappling seminars, in-house tournaments and fight-week hype campaigns without needing an agency each time.' },
    ],
    gallery: ['Brand system','Website','Game Plan app','Member dashboard','MacroBeast sub-brand','Event landing pages'],
  },

  verobill: {
    slug: 'verobill', name: 'VeroBill',
    tagline: 'Create invoices in 30 seconds. Not 30 features.',
    discipline: ['Product', 'Web', 'SaaS'],
    sector: 'SaaS · Invoicing for Freelancers', year: '2024–2025',
    accent: '#34D399', url: 'verobill.com',
    shot: '/case-shots/verobill.webp',
    card: {
      resultLine: 'Full SaaS product + marketing site + lifetime launch',
      chips: ['Invoices', 'Estimates', 'Recurring', 'Multi-currency'],
      stats: [
        { k: 'Tax regions', v: '12' },
        { k: 'Payment rails', v: '4' },
        { k: 'Lifetime deal', v: '$99' },
      ],
      callout: '"Free tier that works forever. Lifetime deal for the subscription-averse."',
    },
    brief: "A bootstrapped SaaS taking on FreshBooks, Invoicely and Billdu with a radical simplicity thesis: everything a freelancer actually needs, nothing they don't. Needed a product that felt instant, a marketing site that made the value obvious in under 10 seconds, and a lifetime-deal launch capable of running alongside the free tier.",
    built: [
      'End-to-end product — invoicing, estimates, recurring, expenses, P&L',
      'Multi-currency + 12-region tax templates (auto-fill VAT, GST, CGST+SGST, Sales Tax)',
      'Payment integrations — Stripe, PayPal, Razorpay UPI, Wise on every invoice',
      'Comparison marketing page (VeroBill vs Billdu vs Invoicely vs FreshBooks)',
      'Lifetime deal launch flow — 150-spot cap + live counter',
      'PWA-installable mobile experience',
      'AI-powered SEO blog engine built into the product',
    ],
    what: 'A product that treats freelancers like adults. Free tier that works forever, lifetime deal for the ones who hate subscriptions, and marketing copy that reads like a person, not a growth team.',
    proof: 'Live at verobill.com. Free plan, Pro plan, $99 lifetime deal all in market. Comparison table published against 3 competitors with public April 2026 pricing sources.',
    hero: {
      eyebrow: 'SaaS · Invoicing for freelancers',
      title: 'Create invoices in 30 seconds. Not 30 features.',
      titleItalicWord: 'seconds.',
      sub: 'Free forever plan. $99 lifetime deal for the ones who hate subscriptions. Multi-currency, 12-region tax templates, Stripe / PayPal / Razorpay / Wise.',
      ctas: [{ l: 'Start free', primary: true }, { l: 'Grab lifetime · $99' }],
      pills: ['Invoices','Estimates','Recurring','Expenses','P&L','Multi-currency'],
    },
    highlights: [
      { h: '12-region tax templates', p: 'VAT, GST, CGST+SGST, Sales Tax — the correct tax fields auto-fill by region. A freelancer in Mumbai and one in Manchester both get the right invoice in one click.' },
      { h: 'Lifetime, not subscription', p: 'A 150-spot lifetime deal at $99 running alongside the free + pro plans — with a live spot counter and a comparison table against FreshBooks, Billdu, Invoicely.' },
      { h: 'Every payment method that matters', p: 'Stripe, PayPal, Razorpay UPI and Wise on every invoice. Freelancers don&rsquo;t chase — clients just pay.' },
    ],
    gallery: ['Product UI','Comparison page','Lifetime deal flow','Multi-currency','Blog engine','Mobile PWA'],
  },
};
export const CASE_ORDER = ['sugaspice', 'brightridge', 'cobra-thai', 'verobill'];
